import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  ShoppingBag, 
  DollarSign, 
  Activity, 
  Users, 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import Chart from '../Chart';
import { API_BASE_URL } from '../../config';

const Dashboard = ({ setActiveTab }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
        setLastUpdated(new Date());
      } else {
        throw new Error(data.error || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      setError(err.message);
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'processing': 'bg-purple-100 text-purple-800',
      'shipped': 'bg-indigo-100 text-indigo-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'refunded': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'info':
        return <Eye className="h-5 w-5 text-blue-500" />;
      default:
        return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No dashboard data available</p>
      </div>
    );
  }

  const { stats, recent_orders, top_products, low_stock_products, recent_customers, alerts } = dashboardData;

  // Handle both old and new data structures
  const ordersData = stats?.orders || {
    total: stats?.total_orders || 0,
    pending: stats?.pending_orders || 0,
    confirmed: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0
  };

  const revenueData = stats?.revenue || {
    total: stats?.total_revenue || 0,
    this_month: 0,
    avg_order_value: stats?.total_revenue && stats?.total_orders ? 
      stats.total_revenue / stats.total_orders : 0
  };

  const paymentsData = stats?.payments || {
    paid: stats?.paid_orders || 0,
    unpaid: (stats?.total_orders || 0) - (stats?.paid_orders || 0)
  };

  const productsData = stats?.products || {
    total: stats?.total_products || 0,
    active: stats?.active_products || 0,
    inactive: 0,
    draft: 0,
    low_stock: stats?.low_stock_products || 0,
    out_of_stock: 0,
    in_stock: (stats?.total_products || 0) - (stats?.low_stock_products || 0)
  };

  const customersData = stats?.customers || {
    total: 0,
    verified: 0,
    unverified: 0,
    new_this_month: 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Overview of your store performance</p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdated && (
            <p className="text-sm text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                alert.type === 'error' ? 'bg-red-50 border-red-200' :
                alert.type === 'info' ? 'bg-blue-50 border-blue-200' :
                'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getAlertIcon(alert.type)}
                  <div>
                    <h4 className="font-medium text-gray-900">{alert.title || 'Alert'}</h4>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                  </div>
                </div>
                {alert.action && (
                  <button 
                    onClick={() => {
                      if (alert.action === 'View Products') {
                        setActiveTab('products');
                      } else if (alert.action === 'Review Carts') {
                        setActiveTab('orders');
                      } else if (alert.action === 'Process Orders') {
                        setActiveTab('orders');
                      }
                    }}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                  >
                    {alert.action}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Orders */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(ordersData.total)}
              </p>
              {revenueData.this_month > 0 && (
                <p className="text-sm text-green-600 flex items-center mt-1">
                  <ArrowUpRight className="h-4 w-4 mr-1" />
                  {formatNumber(ordersData.this_month)} this month
                </p>
              )}
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <ShoppingBag className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(revenueData.total)}
              </p>
              {revenueData.this_month > 0 && (
                <p className="text-sm text-green-600 flex items-center mt-1">
                  <ArrowUpRight className="h-4 w-4 mr-1" />
                  {formatCurrency(revenueData.this_month)} this month
                </p>
              )}
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(productsData.total)}
              </p>
              <p className="text-sm text-gray-500">
                {formatNumber(productsData.active)} active
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Package className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Customers */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(customersData.total)}
              </p>
              {customersData.new_this_month > 0 && (
                <p className="text-sm text-green-600 flex items-center mt-1">
                  <ArrowUpRight className="h-4 w-4 mr-1" />
                  {formatNumber(customersData.new_this_month)} new this month
                </p>
              )}
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Status Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Order Status</h3>
          <div className="space-y-3">
            {Object.entries(ordersData).map(([status, count]) => {
              if (status === 'total' || status === 'this_month') return null;
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      status === 'pending' ? 'bg-yellow-400' :
                      status === 'confirmed' ? 'bg-blue-400' :
                      status === 'processing' ? 'bg-purple-400' :
                      status === 'shipped' ? 'bg-indigo-400' :
                      status === 'delivered' ? 'bg-green-400' :
                      status === 'cancelled' ? 'bg-red-400' : 'bg-gray-400'
                    }`} />
                    <span className="text-sm font-medium capitalize">{status}</span>
                  </div>
                  <span className="text-sm text-gray-600">{formatNumber(count)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Product Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Product Status</h3>
          <div className="space-y-3">
            {Object.entries(productsData).map(([status, count]) => {
              if (status === 'total' || status === 'total_inventory' || status.includes('avg')) return null;
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      status === 'active' ? 'bg-green-400' :
                      status === 'inactive' ? 'bg-gray-400' :
                      status === 'draft' ? 'bg-yellow-400' :
                      status === 'low_stock' ? 'bg-orange-400' :
                      status === 'out_of_stock' ? 'bg-red-400' :
                      status === 'in_stock' ? 'bg-blue-400' : 'bg-gray-400'
                    }`} />
                    <span className="text-sm font-medium capitalize">
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">{formatNumber(count)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Order Value</span>
              <span className="text-sm font-medium">
                {formatCurrency(revenueData.avg_order_value)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Paid Orders</span>
              <span className="text-sm font-medium">
                {formatNumber(paymentsData.paid)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Unpaid Orders</span>
              <span className="text-sm font-medium">
                {formatNumber(paymentsData.unpaid)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Low Stock Products</span>
              <span className="text-sm font-medium">
                {formatNumber(productsData.low_stock)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Recent Orders</h3>
            <button 
              onClick={() => setActiveTab('orders')}
              className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {recent_orders && recent_orders.length > 0 ? (
              recent_orders.map((order) => (
                <div key={order.order_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{order.order_id}</p>
                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                    <p className="text-xs text-gray-500">{order.customer_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-sm">{formatCurrency(order.total_amount)}</p>
                    <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(order.order_status)}`}>
                      {order.order_status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ShoppingBag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No orders yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Top Products</h3>
            <button 
              onClick={() => setActiveTab('products')}
              className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {top_products && top_products.length > 0 ? (
              top_products.map((product) => (
                <div key={product.product_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{product.name}</p>
                    <p className="text-sm text-gray-600">
                      Stock: {formatNumber(product.stock)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {product.order_count || 0} orders
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-sm">
                      {formatCurrency(product.sale_price || product.base_price || 0)}
                    </p>
                    <span className={`inline-block px-2 py-1 text-xs rounded ${
                      product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.status || 'active'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No products yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Low Stock Alerts */}
      {productsData.low_stock > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-orange-800">Low Stock Alert</h3>
            <button 
              onClick={() => setActiveTab('products')}
              className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              View All Products
            </button>
          </div>
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              <div>
                <p className="font-medium text-orange-800">
                  {productsData.low_stock} products are low in stock
                </p>
                <p className="text-sm text-orange-600">
                  Consider restocking these products to avoid stockouts
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Status Chart */}
        <Chart
          title="Product Status Distribution"
          type="pie"
          height={250}
          data={[
            { label: 'Active', value: productsData.active },
            { label: 'Inactive', value: productsData.inactive },
            { label: 'Draft', value: productsData.draft },
            { label: 'Low Stock', value: productsData.low_stock },
            { label: 'Out of Stock', value: productsData.out_of_stock }
          ].filter(item => item.value > 0)}
        />
        
        {/* Order Status Chart */}
        <Chart
          title="Order Status Overview"
          type="bar"
          height={250}
          data={Object.entries(ordersData)
            .filter(([status, count]) => status !== 'total' && status !== 'this_month' && count > 0)
            .map(([status, count]) => ({
              label: status.charAt(0).toUpperCase() + status.slice(1),
              value: count
            }))}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => setActiveTab('products')}
            className="p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <Package className="h-6 w-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Add Product</p>
                <p className="text-sm text-blue-600">Create new product listing</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => setActiveTab('orders')}
            className="p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <ShoppingBag className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">View Orders</p>
                <p className="text-sm text-green-600">Manage customer orders</p>
              </div>
            </div>
          </button>
          
          <button 
            onClick={() => setActiveTab('customers')}
            className="p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <Users className="h-6 w-6 text-purple-600" />
              <div>
                <p className="font-medium text-purple-800">Customers</p>
                <p className="text-sm text-purple-600">View customer database</p>
              </div>
            </div>
          </button>

          <button 
            onClick={() => setActiveTab('categories')}
            className="p-4 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors text-left cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <Activity className="h-6 w-6 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800">Categories</p>
                <p className="text-sm text-orange-600">Manage product categories</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;